import { useGPUStats } from '@/hooks/useGPU'
import { Card } from '@/admin-design/_common'

// Threshold bars (>90% red / >70% amber / <70% green/violet) and temperature
// color steps are KEPT RAW: they encode ordinal data-viz thresholds, not
// generic UI badges.
function Bar({
  label,
  value,
  max,
  unit,
  color = 'bg-blue-500',
}: {
  label: string
  value: number
  max: number
  unit: string
  color?: string
}) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0
  const barColor =
    pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : color
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">
          {value}
          {unit && `/${max}${unit}`}
          <span className="ml-1 text-muted-foreground">({pct.toFixed(0)}%)</span>
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

export default function GPUPanel() {
  const { data: stats, isLoading, isError } = useGPUStats()

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="h-4 w-24 bg-muted rounded animate-pulse mb-3" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-6 bg-muted rounded animate-pulse" />
          ))}
        </div>
      </Card>
    )
  }

  if (isError || !stats) {
    return (
      <div className="border border-warning-soft rounded-lg bg-warning-soft p-3">
        <p className="text-xs text-warning-soft-foreground">
          GPU stats unavailable. The GPU server may be offline.
        </p>
      </div>
    )
  }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-card-foreground">GPU</h3>
        <span className="text-xs text-muted-foreground">{stats.name}</span>
      </div>
      <div className="space-y-2.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Temperature</span>
          <span
            className={`font-mono font-medium ${
              stats.temperature_c > 80
                ? 'text-red-600'
                : stats.temperature_c > 65
                  ? 'text-amber-600'
                  : 'text-foreground'
            }`}
          >
            {stats.temperature_c}C
          </span>
        </div>
        <Bar
          label="GPU Usage"
          value={stats.utilization_gpu_pct}
          max={100}
          unit="%"
          color="bg-emerald-500"
        />
        <Bar
          label="VRAM"
          value={stats.memory_used_mb}
          max={stats.memory_total_mb}
          unit=" MB"
          color="bg-violet-500"
        />
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Power</span>
          <span className="font-mono text-foreground">
            {stats.power_draw_w}W / {stats.power_limit_w}W
          </span>
        </div>
      </div>
    </Card>
  )
}
